import React, { useState } from 'react';
import { Plus, Trash2, Save, X } from 'lucide-react';
import { createBill } from '../../../../services/api';

const CreateBill = ({ onSubmit, onCancel, isLoading = false }) => {
  const [formData, setFormData] = useState({
    clientName: '',
    items: [
      {
        name: '',
        quantity: '',
        rate: '',
        total: 0
      }
    ]
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Calculate total for a single item
  const calculateItemTotal = (quantity, rate) => {
    const qty = parseFloat(quantity) || 0;
    const rt = parseFloat(rate) || 0;
    return qty * rt;
  };

  // Calculate grand total of all items
  const calculateGrandTotal = () => {
    return formData.items.reduce((total, item) => {
      return total + calculateItemTotal(item.quantity, item.rate);
    }, 0);
  };

  // Handle input change for client name
  const handleClientNameChange = (e) => {
    setFormData(prev => ({
      ...prev,
      clientName: e.target.value
    }));
    
    // Clear client name error if it exists
    if (errors.clientName) {
      setErrors(prev => ({
        ...prev,
        clientName: ''
      }));
    }
  };

  // Handle input change for items
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };

    // Auto-calculate total when quantity or rate changes
    if (field === 'quantity' || field === 'rate') {
      updatedItems[index].total = calculateItemTotal(
        updatedItems[index].quantity,
        updatedItems[index].rate
      );
    }

    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));

    // Clear item-specific errors
    if (errors[`item_${index}_${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`item_${index}_${field}`]: ''
      }));
    }
  };

  // Add new item
  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        name: '',
        quantity: '',
        rate: '',
        total: 0
      }]
    }));
  };

  // Remove item
  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const updatedItems = formData.items.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        items: updatedItems
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Check client name
    if (!formData.clientName.trim()) {
      newErrors.clientName = 'Client name is required';
    }

    // Check items
    formData.items.forEach((item, index) => {
      if (!item.name.trim()) {
        newErrors[`item_${index}_name`] = 'Item name is required';
      }
      if (!item.quantity || parseFloat(item.quantity) <= 0) {
        newErrors[`item_${index}_quantity`] = 'Valid quantity is required';
      }
      if (!item.rate || parseFloat(item.rate) <= 0) {
        newErrors[`item_${index}_rate`] = 'Valid rate is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Prepare data for submission matching the API structure
      const submitData = {
        clientName: formData.clientName.trim(),
        item: formData.items.map(item => ({
          name: item.name.trim(),
          quantity: parseFloat(item.quantity),
          rate: parseFloat(item.rate),
          total: calculateItemTotal(item.quantity, item.rate)
        })),
        totalAmount: calculateGrandTotal(),
        date: new Date().toISOString()
      };

      const result = await createBill(submitData);
      
      resetForm();
      
      if (onSubmit) {
        onSubmit(result);
      }
      
      alert('Bill created successfully!');
      
    } catch (error) {
      console.error('Error creating bill:', error);
      alert(`Error creating bill: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      clientName: '',
      items: [
        {
          name: '',
          quantity: '',
          rate: '',
          total: 0
        }
      ]
    });
    setErrors({});
  };

  const currentLoading = loading || isLoading;

  return (
    <div className="bg-white p-6 border border-gray-200 shadow-lg rounded-xl">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-6 w-6 text-gray-700"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14,2 14,8 20,8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10,9 9,9 8,9"/>
            </svg>
            <h2 className="text-2xl font-semibold text-gray-800">Create New Bill</h2>
          </div>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X size={20} />
            </button>
          )}
        </div>
        <p className="text-gray-600 mt-1">Create and manage billing information</p>
      </div>

      <div className="space-y-6">
        {/* Client Name */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="space-y-2">
            <label htmlFor="clientName" className="text-sm font-medium text-gray-700">
              Client Name *
            </label>
            <input
              id="clientName"
              name="clientName"
              type="text"
              value={formData.clientName}
              onChange={handleClientNameChange}
              placeholder="Enter client name"
              required
              className={`w-full p-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 ${
                errors.clientName ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={currentLoading}
            />
            {errors.clientName && (
              <p className="text-red-500 text-xs">{errors.clientName}</p>
            )}
          </div>
        </div>

        {/* Items Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-700">Bill Items</h3>
            <button
              type="button"
              onClick={addItem}
              disabled={currentLoading}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus size={16} className="inline mr-1" />
              Add Item
            </button>
          </div>

          {formData.items.map((item, index) => (
            <div key={index} className="p-4 border border-gray-200 rounded-md bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-700">
                  Item {index + 1}
                </h4>
                {formData.items.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    disabled={currentLoading}
                    className="text-red-500 hover:text-red-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Item Name */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Item Name *
                  </label>
                  <input
                    type="text"
                    value={item.name}
                    onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                    placeholder="Enter item name"
                    className={`w-full p-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 ${
                      errors[`item_${index}_name`] ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={currentLoading}
                  />
                  {errors[`item_${index}_name`] && (
                    <p className="text-red-500 text-xs">{errors[`item_${index}_name`]}</p>
                  )}
                </div>

                {/* Quantity */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Quantity *
                  </label>
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                    placeholder="Enter quantity"
                    min="0"
                    step="0.01"
                    className={`w-full p-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 ${
                      errors[`item_${index}_quantity`] ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={currentLoading}
                  />
                  {errors[`item_${index}_quantity`] && (
                    <p className="text-red-500 text-xs">{errors[`item_${index}_quantity`]}</p>
                  )}
                </div>

                {/* Rate */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Rate *
                  </label>
                  <input
                    type="number"
                    value={item.rate}
                    onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                    placeholder="Enter rate"
                    min="0"
                    step="0.01"
                    className={`w-full p-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 ${
                      errors[`item_${index}_rate`] ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={currentLoading}
                  />
                  {errors[`item_${index}_rate`] && (
                    <p className="text-red-500 text-xs">{errors[`item_${index}_rate`]}</p>
                  )}
                </div>

                {/* Total */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Total
                  </label>
                  <div className="w-full p-2.5 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                    ₹{calculateItemTotal(item.quantity, item.rate).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Grand Total */}
        <div className="flex justify-end">
          <div className="bg-gray-100 p-4 rounded-md border border-gray-200">
            <div className="text-lg font-semibold text-gray-800">
              Grand Total: ₹{calculateGrandTotal().toFixed(2)}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="pt-4 space-y-3">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={currentLoading}
            className={`w-full py-3 rounded-md transition-colors duration-200 font-medium ${
              currentLoading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gray-800 hover:bg-gray-700'
            } text-white`}
          >
            <Save size={16} className="inline mr-2" />
            {currentLoading ? 'Creating Bill...' : 'Create Bill'}
          </button>
          
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={resetForm}
              disabled={currentLoading}
              className="py-2 px-4 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              Reset Form
            </button>
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                disabled={currentLoading}
                className="py-2 px-4 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateBill;